import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Box } from "@mui/system";
import { useDataProvider, useInput } from "react-admin";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useWatchMainChanged } from "./Aman/OdsSearch/vehicle";

// ==============================
// Wrapper: computes visibility; no useController here
// ==============================
export const SchoolAutocomplete = (meta) => {
  const { source_name, field_meta } = meta;
  const { control, unregister, clearErrors /*, setValue*/ } = useFormContext();

  // parse config once
  const config = useMemo(() => {
    try {
      return field_meta ? JSON.parse(field_meta) : {};
    } catch (e) {
      console.error("Error parsing field_meta:", e);
      return {};
    }
  }, [field_meta]);

  // read dependency value synchronously for this render
  const dependColumn = config?.depend ?? null;
  const dependValue = config?.dependValue ?? null;
  const notEqualDependValue = config?.notEqualDependValue ?? null;
  const depVal = useWatch({ control, name: dependColumn });

  // compute visibility
  const shouldShowField = notEqualDependValue
    ? !dependColumn || (depVal !== null && depVal !== notEqualDependValue)
    : !dependColumn || (depVal === dependValue);

  // when hidden: unregister + clear errors
  useEffect(() => {
    if (!shouldShowField) {
      // keepValue:true preserves last value in RHF state (useful if it becomes visible again)
      unregister(source_name, { keepValue: true });

      // Clear any lingering errors for this field
      clearErrors(source_name);

      // If you prefer to also clear the stored value, uncomment this:
      // setValue(source_name, undefined, { shouldValidate: false, shouldDirty: false });
    }
  }, [shouldShowField, source_name, unregister, clearErrors /*, setValue*/]);

  if (!shouldShowField) return null;

  return <SchoolAutocompleteInner {...meta} config={config} />;
};

// ==============================
// Inner: mounts ONLY when visible; useController lives here
// ==============================
const SchoolAutocompleteInner = ({ source_name, is_required, field_meta, watched_column, config }) => {
  const dataProvider = useDataProvider();
  const { control, watch, setValue } = useFormContext();

  // react-admin helper for external disabled state
  const { disabled } = watched_column
    ? useWatchMainChanged(watched_column)
    : { disabled: false };

  // controller: registers only when visible
  const { field, fieldState } = useController({
    name: source_name,
    rules: { required: is_required ? "This field is required." : false },
    control,
  });

  // local state for Autocomplete
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fieldValue = watch(source_name);

  // ---- Fetchers ----
  const fetchData = async (searchValue) => {
    setLoading(true);
    try {
      const filters = {};

      if (searchValue && searchValue.length >= 2) {
        if (config.searchSource) {
          filters[config.searchSource] = searchValue;
        }
      }

      // Apply extra filters driven by other watched values
      if (config.filters) {
        Object.entries(config.filters).forEach(([key, { column, operator }]) => {
          const filterValue = watch(key);
          if (filterValue !== undefined && filterValue !== null && filterValue !== "") {
            filters[`${column}@${operator}`] = filterValue;
          }
        });
      }

      const { data } = await dataProvider.getList(config.reference_resource, {
        pagination: { page: 1, perPage: 20 },
        filter: filters,
        sort: { field: config.sortField, order: "ASC" },
      });

      setOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectedOption = async (id) => {
    if (!id) {
      setSelectedOption(null);
      return;
    }
    try {
      const { data } = await dataProvider.getList(config.reference_resource, {
        pagination: { page: 1, perPage: 1 },
        filter: { [`${config.idField}@_eq`]: id },
      });
      if (Array.isArray(data) && data.length > 0) {
        setSelectedOption(data[0]);
        setValue(source_name, data[0][config.idField], { shouldValidate: true });
      } else {
        setSelectedOption(null);
      }
    } catch (error) {
      console.error("Error fetching selected option:", error);
      setSelectedOption(null);
    }
  };

  // ---- Effects ----
  // initial and search-driven loads
  useEffect(() => {
    if (search === "" || search.length >= 2) {
      fetchData(search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // force a base load once on mount (without search filter)
  useEffect(() => {
    fetchData("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep selectedOption in sync when fieldValue changes externally
  useEffect(() => {
    if (fieldValue && (!selectedOption || fieldValue !== selectedOption?.[config.idField])) {
      fetchSelectedOption(fieldValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  // ---- Render ----
  const secondaryFields = Array.isArray(config.secondaryLabelFields) ? config.secondaryLabelFields : [];

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={selectedOption || null}
      onInputChange={(e, value) => setSearch(value)}
      onChange={(e, value) => {
        setSelectedOption(value);
        const val = value?.[config.idField] || "";
        setValue(source_name, val, { shouldValidate: true });
        field.onChange(val);
      }}
      getOptionLabel={(option) => {
        const label = option?.[config.reference_source] || "";
        // If you want to show secondary labels, uncomment:
        // const secondary = secondaryFields.map((f) => option?.[f]).filter(Boolean).join(" - ");
        // return secondary ? `${label} (${secondary})` : label;
        return label;
      }}
      isOptionEqualToValue={(option, value) =>
        option?.[config.idField] === value?.[config.idField]
      }
      readOnly={disabled}
      renderOption={(props, option) => (
        <li {...props} key={option[config.idField]}>
          <div>
            <strong>{option[config.reference_source]}</strong>
            <br />
            <small>{secondaryFields.map((f) => option?.[f]).filter(Boolean).join(" - ")}</small>
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={field.ref}
          label={config.placeholder || "Select a School"}
          variant="outlined"
          disabled={disabled}
          fullWidth
          required={!!is_required}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
        />
      )}
    />
  );
};






export const SchoolAutocompleteFilter = ({
  source,
  resource,
  label,
  field_meta,
  validate,
  is_required
}) => {
  const dataProvider = useDataProvider();
  const { field } = useInput({ source });

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);

  const config = useMemo(() => {
    try {
      return field_meta ? JSON.parse(field_meta) : {};
    } catch (error) {
      console.error("Error parsing field_meta:", error);
      return {};
    }
  }, [field_meta]);

  const fetchData = async (searchValue) => {
    setLoading(true);
    try {
      const { data } = await dataProvider.getList(config.reference_resource, {
        pagination: { page: 1, perPage: 20 },
        filter: { [config.searchSource]: searchValue },
      });
      setOptions(data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setOptions([]);
    }
    setLoading(false);
  };

  const fetchSelectedOption = async (id) => {
    if (!id) {
      setSelectedOption(null);
      return;
    }
    try {
      const { data } = await dataProvider.getList(config.reference_resource, {
        pagination: { page: 1, perPage: 1 },
        filter: { [config.idField]: id },
      });

      if (data.length > 0) {
        setSelectedOption(data[0]);
        setInputValue(source, data[0][config.idField], { shouldValidate: true });
      } else {
        setSelectedOption(null);
      }
    } catch (error) {
      console.error("Error fetching selected option:", error);
      setSelectedOption(null);
    }
  };


  useEffect(() => {
    if (!field.value) {
      setSelectedOption(null);
      setInputValue('');
    }
  }, [field.value]);



  useEffect(() => {
    if (inputValue.length < 2) return;
    fetchData(inputValue);
  }, [inputValue]);


  useEffect(() => {
    if (field.value && (!selectedOption || field.value !== selectedOption?.[config.idField])) {
      fetchSelectedOption(field.value);
    }
  }, [field.value]);


  const handleChange = (event, newValue) => {
    setSelectedOption(newValue);
    setInputValue(newValue ? newValue[config.reference_source] : '');
    field.onChange(newValue ? newValue[config.idField].toString() : '');
  };



  return (
    <Box sx={{ width: '250px !important' }}>
      <Autocomplete
        options={options}
        loading={loading}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(e, val) => setInputValue(val)}
        onChange={handleChange}
        getOptionLabel={(option) => {
          const mainLabel = option[config.reference_source] || "";
          const secondaryLabels = (config.secondaryLabelFields || [])
            .map((f) => option[f])
            .filter(Boolean)
            .join(" - ");
          // return secondaryLabels ? `${mainLabel} (${secondaryLabels})` : mainLabel;
          return mainLabel;
        }}
        isOptionEqualToValue={(option, value) =>
          option[config.idField]?.toString() === value[config.idField]?.toString()
        }
        renderOption={(props, option) => (
          <li {...props} key={option[config.idField]}>
            <div>
              <strong>{option[config.reference_source]}</strong>
              <br />
              <small>
                {config.secondaryLabelFields.map((f) => option[f]).join(" - ")}
              </small>
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={config.placeholder || "Select a School"}
            variant="outlined"
            fullWidth
          />
        )}
      />
    </Box>
  );
};




export default SchoolAutocomplete;
